import { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  addStandardFormSchema,
  type AddStandardFormData,
} from '@/schema/template';
import { useToast } from '@/hooks/use-toast';
import { HierarchyDropdowns } from './components/hierarchy-dropdowns';
import { BilingualTextAreas } from './components/bilingual-text-areas';
import { StandardsList } from './components/standards-list';
import { useBatchCreateStandardsMutation } from '@/hooks/queries/useTemplates';
import { useCanManageTemplates } from '@/hooks/usePermissions'; 

export default function AddStandardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);
  const canManage = useCanManageTemplates();

  // Redirect admin users who try to access this page manually
  useEffect(() => {
    if (!canManage) {
      toast({
        title: t('common.accessDenied', 'Access Denied'),
        description: t('common.noPermission', 'You do not have permission to access this page'),
        variant: 'destructive',
      });
      navigate(`/admin/templates/${id}`, { replace: true });
    }
  }, [canManage, navigate, id, toast, t]);
  const [standardsList, setStandardsList] = useState<AddStandardFormData[]>([]);

  // Property type ID
  const propertyTypeId = id || '1';

  // Batch create standards mutation
  const batchCreateMutation = useBatchCreateStandardsMutation({
    onSuccessCallback: () => {
      // All standards created successfully
      toast({
        title: t('templates.admin.addStandards.toasts.allStandardsSaved'),
        description: t(
          'templates.admin.addStandards.toasts.allStandardsSavedMessage',
          { count: standardsList.length }
        ),
      });

      // Navigate back after saving
      navigate(`/admin/templates/${id}`);
    },
  });

  const form = useForm<AddStandardFormData>({
    resolver: zodResolver(addStandardFormSchema),
    defaultValues: {
      department: '',
      touchpoint: '',
      subtouchpoint: 'none',
      title: '',
      standardTextEnglish: '',
      standardTextArabic: '',
      answerType: 'YNNA',
      commentRequired: false,
    },
  });

  const watchedDepartment = form.watch('department');
  const watchedTouchpoint = form.watch('touchpoint');

  const handleBack = () => {
    navigate(`/admin/templates/${id}`);
  };

  const removeStandard = (index: number) => {
    setStandardsList(prev => prev.filter((_, i) => i !== index));
  };

  const saveAllStandards = () => {
    if (standardsList.length === 0) {
      toast({
        title: t('templates.admin.addStandards.toasts.noStandards'),
        description: t(
          'templates.admin.addStandards.toasts.noStandardsMessage'
        ),
        variant: 'destructive',
      });
      return;
    }

    // Prepare batch payload
    const batchPayload = {
      standards: standardsList.map(data => ({
        property_type_id: Number(propertyTypeId),
        department_id: Number(data.department),
        touchpoint_id: Number(data.touchpoint),
        sub_touchpoint_id:
          data.subtouchpoint === 'none' ? null : Number(data.subtouchpoint),
        title_id: Number(data.title),
        question_en: data.standardTextEnglish,
        question_ar: data.standardTextArabic,
      })),
    };

    // Call batch API
    batchCreateMutation.mutate(batchPayload);
  };

  const onSubmit = (data: AddStandardFormData) => {
    // Add to local list for display (no API call yet)
    setStandardsList(prev => [...prev, data]);

    // Show success toast
    toast({
      title: t('templates.admin.addStandards.toasts.standardAdded'),
      description: t(
        'templates.admin.addStandards.toasts.standardAddedMessage'
      ),
    });

    // Reset only the text areas to add more standards quickly
    form.reset({
      department: data.department,
      touchpoint: data.touchpoint,
      subtouchpoint: data.subtouchpoint,
      title: data.title,
      standardTextEnglish: '',
      standardTextArabic: '',
      answerType: 'YNNA',
      commentRequired: false,
    });
  };

  return (
    <div className={cn('min-h-screen bg-background')} dir="ltr">
      {/* Header */}
      <div className="bg-primary text-primary-foreground p-3 sm:p-4 lg:p-6">
        <div className="max-w-4xl mx-auto">
          <div
            className={cn(
              'flex flex-col xs:flex-row xs:items-center gap-2 xs:gap-4',
              isRTL ? 'xs:flex-row-reverse' : 'xs:flex-row'
            )}
          >
            <Button
              variant="ghost"
              onClick={handleBack}
              className="text-primary-foreground hover:bg-primary-foreground/10 self-start xs:self-auto"
              size="sm"
            >
              {isRTL ? (
                <>
                  <span className="text-xs sm:text-sm">
                    {t('templates.admin.addStandards.backToStandards')}
                  </span>
                  <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2 rotate-180" />
                </>
              ) : (
                <>
                  <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="text-xs sm:text-sm">
                    {t('templates.admin.addStandards.backToStandards')}
                  </span>
                </>
              )}
            </Button>
            <h1
              className={cn(
                'text-lg sm:text-xl lg:text-2xl font-semibold truncate',
                isRTL && 'text-right'
              )}
            >
              {t('templates.admin.addStandards.pageTitle')}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-3 sm:p-4 lg:p-6">
        <Card>
          <CardHeader className="p-3 sm:p-4 lg:p-6">
            <CardTitle
              className={cn(
                'flex items-center gap-2 text-base sm:text-lg',
                isRTL ? 'flex-row-reverse text-right' : 'flex-row'
              )}
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              {t('templates.admin.addStandards.addNewStandard')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 lg:p-6">
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4 sm:space-y-6"
            >
              <div
                className={cn(
                  'text-center py-2 sm:py-4',
                  isRTL && 'text-right'
                )}
              >
                <p className="text-gray-500 mb-4 sm:mb-6 text-sm sm:text-base px-4">
                  {t(
                    'templates.admin.addStandards.selectDepartmentInstruction'
                  )}
                </p>
              </div>

              {/* Hierarchy Dropdowns - Now uses API */}
              <HierarchyDropdowns
                control={form.control}
                errors={form.formState.errors}
                setValue={form.setValue}
                propertyTypeId={propertyTypeId}
                watchedDepartment={watchedDepartment}
                watchedTouchpoint={watchedTouchpoint}
              />

              {/* Standard Text Fields */}
              <BilingualTextAreas
                control={form.control}
                errors={form.formState.errors}
              />

              {/* Form Actions */}
              <div
                className={cn(
                  'flex flex-col xs:flex-row items-stretch xs:items-center gap-2 sm:gap-4 pt-4 sm:pt-6 border-t',
                  isRTL ? 'xs:flex-row-reverse' : 'xs:flex-row'
                )}
              >
                <Button
                  type="submit"
                  size="sm"
                  className="w-full xs:w-auto text-xs sm:text-sm"
                >
                  <Plus
                    className={cn(
                      'w-3 h-3 sm:w-4 sm:h-4',
                      isRTL ? 'ml-1 sm:ml-2' : 'mr-1 sm:mr-2'
                    )}
                  />
                  {t('templates.admin.addStandards.form.addToList')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full xs:w-auto text-xs sm:text-sm"
                  onClick={() =>
                    form.reset({
                      department: '',
                      touchpoint: '',
                      subtouchpoint: 'none',
                      title: '',
                      standardTextEnglish: '',
                      standardTextArabic: '',
                      answerType: 'YNNA',
                      commentRequired: false,
                    })
                  }
                >
                  {t('templates.admin.addStandards.form.clearAll')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full xs:w-auto text-xs sm:text-sm"
                  onClick={handleBack}
                >
                  {t('templates.admin.addStandards.form.cancel')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Standards List */}
        <StandardsList
          standards={standardsList}
          onRemove={removeStandard}
          onSaveAll={saveAllStandards}
          propertyTypeId={propertyTypeId}
        />
      </div>
    </div>
  );
}
